import React from 'react';
import { SavedScreeningProse, savedScreeningDisplayText } from './SavedScreeningProse';
import { ArrowRight } from 'lucide-react';
import type { CallReadyAnswer, CallReadyAnswerLine, CallReadyRiskItem } from '../../../lib/findings/callReadyFindings';
import { Button } from '../../ui/button';
import type { SiteEntity } from '../../../types/site';
import type { SiteRagReport } from '../../../types/siteRag';
import { analysisReadinessDecisionV1Schema } from '../../../types/decisionAnalysis';
import { reportCanContributeSavedResult } from '../../../lib/pathToGo/reportPlan';
import { hashProjectIntent } from '../../../lib/sed/shouldBootstrap';
import { SCREENING_ARCHITECTURE_LINE_IDS } from '../../../lib/findings/screeningArchitectureRecommendations';
import {
  derivePresentedScreeningRecommendationSetV1,
  deriveScreeningRecommendationsForPresentation,
} from '../../../lib/findings/screeningRecommendationsForPresentation';

const BASIS_LABEL: Record<CallReadyAnswerLine['basis'], string> = {
  recorded: 'Recorded choice',
  recommended: 'Recommended option',
  'not-determined': 'Still to determine',
};

const HOME_LABEL: Record<string, string> = {
  'btm-architecture': 'BTM architecture',
  'power-type': 'Power type',
  'dc-power': 'DC load',
  'cooling-strategy': 'Rack cooling',
  'cooling-type': 'Heat rejection',
};

const recommendationLabel = (line: CallReadyAnswerLine): string =>
  line.id === 'dc-power' ? 'DC size range' : HOME_LABEL[line.id] ?? line.label;

/** Select a completed saved screen, preserving the shared advisory hold gates. */
export function selectCompletedScreeningReport(reports: readonly SiteRagReport[], siteId?: string): SiteRagReport | null {
  const eligible = reports.filter(report => {
    if (siteId != null && report.siteId != null && report.siteId !== siteId) return false;
    if (!reportCanContributeSavedResult(report, 'feasibility')) return false;
    if (report.qualityHold === 'needs-revision' || (report.qualityHoldFindings?.length ?? 0) > 0) return false;
    const raw = report.analysisReadiness ?? report.analysisRevision?.readiness;
    if (raw == null) return true;
    const parsed = analysisReadinessDecisionV1Schema.safeParse(raw);
    return parsed.success && parsed.data.canView && parsed.data.identityValid
      && parsed.data.qualityReady && parsed.data.narrativeReady
      && ((parsed.data.ready && parsed.data.canUseForDecision)
        || parsed.data.state === 'queued' || parsed.data.state === 'refreshing');
  });
  const recency = (report: SiteRagReport): number => {
    const time = Date.parse(report.updatedAt ?? report.createdAt);
    return Number.isFinite(time) ? time : 0;
  };
  return eligible.reduce<SiteRagReport | null>((latest, report) =>
    !latest || recency(report) > recency(latest) ? report : latest, null);
}

export function describeScreeningBasisQualification(
  screening: SiteRagReport,
  liveSite?: Pick<SiteEntity, 'sedId' | 'sedVersion' | 'projectIntent'> | null,
): { isHistorical: boolean; basisSummary: string } {
  const versionLabel = screening.version != null
    ? `v${screening.version}${screening.minorVersion != null ? `.${screening.minorVersion}` : ''}`
    : 'saved revision';
  const sedLabel = screening.sedVersionUsed != null ? `SED v${screening.sedVersionUsed}` : null;
  const modeAtGen = screening.projectIntentAtGen?.screeningMode === 'evaluate-proposal'
    ? 'Evaluate-my-proposal mode'
    : screening.projectIntentAtGen?.screeningMode === 'recommend'
      ? 'Recommend-an-approach mode'
      : null;
  const basisParts = [versionLabel, sedLabel, modeAtGen].filter(Boolean).join(' · ');
  const readiness = screening.analysisReadiness ?? screening.analysisRevision?.readiness;
  const modeChanged = Boolean(
    liveSite?.projectIntent?.screeningMode
    && screening.projectIntentAtGen?.screeningMode
    && liveSite.projectIntent.screeningMode !== screening.projectIntentAtGen.screeningMode,
  );
  const intentChanged = Boolean(
    liveSite?.projectIntent
    && screening.projectIntentAtGen
    && hashProjectIntent(liveSite.projectIntent) !== hashProjectIntent(screening.projectIntentAtGen),
  );
  const sedChanged = Boolean(
    (liveSite?.sedId && screening.sedId && liveSite.sedId !== screening.sedId)
    || (liveSite?.sedVersion != null && screening.sedVersionUsed != null && liveSite.sedVersion !== screening.sedVersionUsed),
  );
  const isHistorical = Boolean(
    screening.requiresRegeneration != null
    || (readiness && readiness.current === false)
    || modeChanged
    || intentChanged
    || sedChanged,
  );
  if (isHistorical) {
    const reason = modeChanged
      ? `saved in ${modeAtGen ?? 'a different mode'} while live Intent is ${liveSite?.projectIntent?.screeningMode}`
      : intentChanged
        ? 'Project Intent changed since this screening was saved'
        : sedChanged
          ? `saved on ${sedLabel ?? 'earlier site evidence'} while live evidence is SED v${liveSite?.sedVersion ?? '?'}`
          : 'inputs or site evidence changed since this screening was saved';
    return {
      isHistorical: true,
      basisSummary: `Historical saved screening basis (${basisParts} — ${reason}). Refresh Screening Assessment before treating as current live advice.`,
    };
  }
  return {
    isHistorical: false,
    basisSummary: `Saved screening basis: ${basisParts}.`,
  };
}

/** Saved screening outcomes lead; recorded Intent choices remain separate. */
export function SiteRecommendedPath({ answer, screeningReports = [], siteId, liveSite, risks = [], loading = false, onReview }: {
  answer?: CallReadyAnswer;
  screeningReports?: readonly SiteRagReport[];
  siteId?: string;
  liveSite?: Pick<SiteEntity, 'sedId' | 'sedVersion' | 'projectIntent'> | null;
  risks?: readonly CallReadyRiskItem[];
  loading?: boolean;
  onReview: () => void;
}) {
  const screening = selectCompletedScreeningReport(screeningReports, siteId);
  // Intent, documents, scores and a pending attempt are not a completed screen.
  if (!screening) return null;
  const projection = deriveScreeningRecommendationsForPresentation(screening, siteId);
  const recommendationSet = derivePresentedScreeningRecommendationSetV1(screening, siteId);
  const basisQualification = describeScreeningBasisQualification(screening, liveSite);
  const unresolvedQuestions = SCREENING_ARCHITECTURE_LINE_IDS
    .map(id => recommendationSet.questions[id])
    .filter(q => q.status === 'unresolved' && q.unresolvedReason);
  const lines: CallReadyAnswerLine[] = Object.entries(projection).flatMap(([id, line]) => line ? [{
    id, label: HOME_LABEL[id] ?? id, value: line.value,
    basis: 'recommended' as const, basisNote: line.basisNote,
  }] : []);
  const lead = lines.find(line => line.id === 'power-type')
    ?? lines.find(line => line.id === 'btm-architecture') ?? lines[0];
  const supporting = lines.filter(line => line !== lead);
  const recorded = answer?.lines?.flatMap(line => line.basis === 'recorded' ? [line]
    : line.recordedInput ? [{ id: line.id, label: line.label, ...line.recordedInput, basis: 'recorded' as const }] : []) ?? [];
  return (
    <section className="instrument-panel @container min-w-0 normal-case tracking-normal" aria-label="Recommended path" aria-busy={loading} data-testid="site-home-recommended-path">
      <header className="flex flex-wrap items-start justify-between gap-4 pb-6">
        <div className="min-w-0 max-w-2xl">
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">Recommended path</h2>
          <p className="mt-2 text-sm leading-6 text-text-secondary">Power, behind-the-meter supply and cooling from the completed screening's saved outcomes.</p>
        </div>
        <Button variant="mission-primary" size="sm" className="min-h-11 max-w-full whitespace-normal text-left normal-case tracking-normal" onClick={onReview} disabled={loading}>
          Review power &amp; cooling <ArrowRight aria-hidden />
        </Button>
      </header>
      {loading ? (
        <p className="py-5 text-sm leading-7 text-text-secondary" role="status">Reading the saved screening recommendations…</p>
      ) : lead ? (
        <div data-testid="site-home-path-overview">
          <div className="border-l-[3px] border-primary bg-primary/5 px-4 py-3 space-y-1">
            <p className="max-w-[78ch] text-sm leading-6 text-text-secondary">Saved screening options require review before adoption. Recorded project inputs remain separate.</p>
            <p className="max-w-[78ch] text-xs leading-5 text-text-secondary" data-testid="site-home-basis-qualification" data-historical={basisQualification.isHistorical ? 'true' : 'false'}>
              {basisQualification.basisSummary}
            </p>
          </div>
          <div className="py-6" data-testid="site-home-path-lead">
            <p className="text-sm leading-6 text-primary">{recommendationLabel(lead)}</p>
            <h3 className="mt-2 max-w-[60ch] text-2xl font-semibold leading-snug tracking-tight text-text-primary [overflow-wrap:anywhere]">{savedScreeningDisplayText(lead.value, false)}</h3>
            <p className="mt-2 text-xs leading-6 text-text-secondary">Recommended option from the saved screening</p>
          </div>
          {supporting.length > 0 && <dl aria-label="Supporting screening recommendations" className="grid min-w-0 gap-x-6 border-t border-border @[36rem]:grid-cols-2 @[56rem]:grid-cols-3">
            {supporting.map(line => <div key={line.id} className="min-w-0 border-b border-border py-5">
              <dt className="text-sm leading-6 text-text-secondary">{recommendationLabel(line)}</dt>
              <dd className="mt-2 text-base font-semibold leading-7 text-text-primary [overflow-wrap:anywhere]">{savedScreeningDisplayText(line.value, false)}</dd>
            </div>)}
          </dl>}
        </div>
      ) : (
        <p className="py-5 text-sm leading-7 text-text-secondary">The completed screening has not recorded a power, cooling or conditional size recommendation. Undecided choices remain open.</p>
      )}
      {!loading && recorded.length > 0 && (
        <section className="mt-1 border-y border-border py-5" aria-label="Recorded project inputs" data-testid="site-home-recorded-choices">
          <h3 className="text-sm font-semibold leading-6 text-text-primary">Recorded inputs</h3>
          <p className="mt-1 text-xs leading-6 text-text-secondary">Retained project choices; these are not screening recommendations.</p>
          <dl className="mt-4 grid min-w-0 gap-5 @[36rem]:grid-cols-2 @[56rem]:grid-cols-3">
            {recorded.map(line => (
              <div key={line.id} className="min-w-0">
                <dt className="text-sm leading-6 text-text-secondary">{HOME_LABEL[line.id] ?? line.label}</dt>
                <dd className="mt-1 text-base font-semibold leading-7 text-text-primary [overflow-wrap:anywhere]">{savedScreeningDisplayText(line.value.replace(/\b(\d{4,})(?=\s*MW\b)/g, amount => Number(amount).toLocaleString('en-US')), false)}</dd>
                <dd className="mt-2 space-y-2">
                  <SavedScreeningProse text={line.basisNote} showReferences={false} className="text-xs leading-6 text-text-secondary" />
                  {line.nextStep && <SavedScreeningProse text={line.nextStep} showReferences={false} className="text-xs leading-6 text-text-secondary" />}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {!loading && (lines.length > 0 || risks.length > 0) && (
        <div className={`mt-7 grid min-w-0 gap-7 ${lines.length > 0 ? '@[56rem]:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] @[56rem]:gap-9' : ''}`}>
          {lines.length > 0 && <section className="min-w-0" aria-label="Saved screening rationale">
            <h3 className="text-base font-semibold leading-7 text-text-primary">Saved rationale</h3>
            <dl className="mt-4 space-y-6">
              {lines.map(line => <div key={line.id} className="min-w-0" data-testid={`site-home-path-${line.id}`}>
                <dt className="text-sm font-medium leading-6 text-text-primary">{recommendationLabel(line)}</dt>
                <dd className="mt-1 min-w-0 space-y-3">
                  <p className="max-w-[78ch] text-sm leading-7 text-primary [overflow-wrap:anywhere]">{savedScreeningDisplayText(line.value, false)}</p>
                  <p className="text-xs leading-6 text-text-secondary">{BASIS_LABEL[line.basis]}</p>
                  {line.nextStep && <SavedScreeningProse text={line.nextStep} showReferences={false} />}
                  <SavedScreeningProse text={line.basisNote} showReferences={false} />
                </dd>
              </div>)}
            </dl>
          </section>}
          <aside className="min-w-0 border-t border-border pt-5 @[56rem]:border-l @[56rem]:border-t-0 @[56rem]:pl-6 @[56rem]:pt-0" aria-label="What needs review" data-testid="site-home-path-constraints">
            <h3 className="text-sm font-semibold leading-6 text-text-primary">What needs review</h3>
            <p className="mt-2 text-xs leading-6 text-text-secondary">Validate the saved conditions before adopting a recommendation. The rationale retains the screening's assumptions and limitations.</p>
            {unresolvedQuestions.length > 0 && <dl className="mt-4 space-y-4">
              {unresolvedQuestions.map(q => (
                <div key={q.questionId} className="min-w-0 space-y-1" data-testid={`site-home-unresolved-${q.questionId}`}>
                  <dt className="text-sm font-medium leading-6 text-text-primary">{q.questionId === 'dc-power' ? 'DC size range' : HOME_LABEL[q.questionId] ?? q.questionId} · Unresolved in saved screening</dt>
                  <dd className="min-w-0 space-y-1">
                    <SavedScreeningProse text={q.unresolvedReason!} showReferences={false} className="text-xs leading-6 text-text-secondary" />
                    {q.nextStep && <SavedScreeningProse text={q.nextStep} showReferences={false} className="text-xs leading-6 text-text-primary" />}
                  </dd>
                </div>
              ))}
            </dl>}
            {risks.length > 0 && <ul className="mt-5 space-y-5">
              {risks.map(risk => (
                <li key={risk.id} className="min-w-0 space-y-2 [overflow-wrap:anywhere]">
                  <h4 className="text-sm font-medium leading-6 text-text-primary">{risk.title}</h4>
                  {risk.state === 'not-assessed' && <p className="text-xs leading-6 text-text-secondary">Not yet assessed</p>}
                  <SavedScreeningProse text={risk.finding} showReferences={false} className="text-xs leading-6 text-text-secondary" />
                  <SavedScreeningProse text={risk.action} showReferences={false} className="text-xs leading-6 text-text-primary" />
                  <SavedScreeningProse text={`Basis: ${risk.source}`} showReferences={false} className="text-xs leading-6 text-text-secondary" />
                </li>
              ))}
            </ul>}
          </aside>
        </div>
      )}
      {!loading && <p className="mt-7 border-t border-border pt-4 text-xs leading-6 text-text-secondary">Recommendations are screening options to review and validate. Recorded choices remain separate. Open power &amp; cooling for the full reasoning, sources and risks.</p>}
    </section>
  );
}
